<!-- eslint-disable vue/no-mutating-props -->
<template>
	<div class="inv-editor no-print" v-if="state">
		<div class="inv-save" v-if="canSave">
			<button type="button" class="btn btn-sm btn-primary" :disabled="saveStatus === 'saving'" @click="$emit('save')">
				{{ saveStatus === 'saving' ? '저장 중…' : '이 물건 기본값으로 저장' }}
			</button>
			<button type="button" class="btn btn-sm btn-outline-secondary" @click="$emit('reset')">기본값 초기화</button>
			<span v-if="saveStatus === 'saved'" class="inv-save-msg ok">저장됨 ✓</span>
			<span v-if="saveStatus === 'error'" class="inv-save-msg err">저장 실패</span>
		</div>
		<div class="section-tit mt-4">투자분석 입력</div>
		<div class="inv-grid">
			<label>취득세율(%)<input type="number" step="0.1" class="form-control form-control-sm" v-model.number="state.taxRate" @change="emit" /></label>
			<label>중개보수(%)<input type="number" step="0.1" class="form-control form-control-sm" v-model.number="state.brokerRate" @change="emit" /></label>
			<label>법무·기타(만원)<input type="number" class="form-control form-control-sm" v-model.number="state.legalFee" @change="emit" /></label>
			<label>LTV(%)<input type="number" class="form-control form-control-sm" v-model.number="state.ltv" @change="emit" /></label>
			<label>금리(%)<input type="number" step="0.1" class="form-control form-control-sm" v-model.number="state.loanRate" @change="emit" /></label>
			<label>연 NOI(만원)<input type="number" class="form-control form-control-sm" v-model.number="state.noi" @change="emit" /></label>
		</div>
		<p class="inv-caution" v-if="residential">※ 주택은 가액·면적·보유수에 따라 1.1~12% 누진·중과 — 취득세율 수동확인 필요.</p>

		<div class="section-tit mt-3">단가 가정</div>
		<div class="inv-grid">
			<label>마감 등급
				<select class="form-control form-control-sm" v-model="state.gradeKey" @change="onGrade">
					<option v-for="g in gradeOptions" :key="g.value" :value="g.value">{{ g.label }}</option>
				</select>
			</label>
			<label>지역
				<select class="form-control form-control-sm" v-model="state.regionKey" @change="emit">
					<option v-for="r in regionOptions" :key="r" :value="r">{{ r }}</option>
				</select>
			</label>
			<label>리모델링 적용비율(%)<input type="number" min="0" max="100" class="form-control form-control-sm" v-model.number="state.remodelRatio" @change="emit" /></label>
		</div>
		<p class="inv-caution">※ 등급 변경 시 표준 항목의 평단가만 재산정됩니다(추가 항목·변경한 항목명은 유지). 지역·적용비율은 단가 유지(즉시 반영).</p>

		<div class="section-tit mt-3">리모델링 항목(만원/평)<button type="button" class="inv-add" @click="addRow('remodel')">+ 항목 추가</button></div>
		<div class="inv-rows">
			<div class="inv-row" v-for="(r, i) in state.remodelRows" :key="r.key">
				<input type="text" class="form-control form-control-sm inv-row-label" v-model="r.label" @change="emit" placeholder="항목명" />
				<input type="number" class="form-control form-control-sm inv-row-unit" v-model.number="r.unit" @change="emit" placeholder="평단가" />
				<button type="button" class="inv-del" @click="removeRow('remodel', i)" title="항목 삭제">✕</button>
			</div>
			<p v-if="!state.remodelRows.length" class="inv-empty">항목이 없습니다. '+ 항목 추가'로 추가하세요.</p>
		</div>

		<div class="section-tit mt-3">신축 항목(만원/평)<button type="button" class="inv-add" @click="addRow('newbuild')">+ 항목 추가</button></div>
		<div class="inv-grid">
			<label>신축 용적률(%)<input type="number" step="0.01" class="form-control form-control-sm" v-model.number="state.newbuildFar" @change="emit" /></label>
		</div>
		<div class="inv-rows">
			<div class="inv-row" v-for="(r, i) in state.newbuildRows" :key="r.key">
				<input type="text" class="form-control form-control-sm inv-row-label" v-model="r.label" @change="emit" placeholder="항목명" />
				<input type="number" class="form-control form-control-sm inv-row-unit" v-model.number="r.unit" @change="emit" placeholder="평단가" />
				<button type="button" class="inv-del" @click="removeRow('newbuild', i)" title="항목 삭제">✕</button>
			</div>
			<p v-if="!state.newbuildRows.length" class="inv-empty">항목이 없습니다. '+ 항목 추가'로 추가하세요.</p>
		</div>
	</div>
</template>

<script>
import { GRADE_OPTIONS, REGION_OPTIONS, regenerateUnits, nextCustomKey } from '@/utils/investment.mjs';

export default {
	name: 'InvestmentSidebarEditor',
	emits: ['change', 'save', 'reset'],
	props: {
		state: { type: Object, default: null },
		residential: { type: Boolean, default: false },
		canSave: { type: Boolean, default: false },
		saveStatus: { type: String, default: '' },
	},
	data() {
		return { gradeOptions: GRADE_OPTIONS, regionOptions: REGION_OPTIONS };
	},
	methods: {
		emit() { this.$emit('change'); },
		// 등급 변경 → 표준행 기준단가만 재생성(커스텀행·변경 라벨 보존) 후 URL 동기화.
		onGrade() { if (this.state) regenerateUnits(this.state); this.$emit('change'); },
		// #5 자금계획 항목 가감. 커스텀 행은 빈 라벨·빈 단가로 추가(rmc{n}/nbc{n} 키).
		addRow(kind) {
			if (!this.state) return;
			const rows = kind === 'newbuild' ? this.state.newbuildRows : this.state.remodelRows;
			rows.push({ key: nextCustomKey(rows, kind), label: '', unit: '' });
			this.$emit('change');
		},
		removeRow(kind, i) {
			if (!this.state) return;
			const rows = kind === 'newbuild' ? this.state.newbuildRows : this.state.remodelRows;
			rows.splice(i, 1);
			this.$emit('change');
		},
	},
};
</script>

<style scoped>
.inv-editor { padding: 0 4px; }
/* 2열 그리드로 좌측 패널 세로 길이 압축(필드 수 동일, 행수 절반). 긴 라벨은 칸 안에서 줄바꿈. */
.inv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 10px; margin-top: 6px; }
.inv-grid label { display: flex; flex-direction: column; font-size: 11px; gap: 2px; min-width: 0; }
.inv-caution { color: #b45309; font-size: 10px; margin-top: 5px; line-height: 1.35; }
.inv-save { display: flex; align-items: center; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
.inv-save-msg { font-size: 11px; }
.inv-save-msg.ok { color: #1f8a4c; }
.inv-save-msg.err { color: #c0392b; }
/* #5 자금계획 항목 행 편집(라벨+평단가+삭제) + 항목추가 */
.inv-add { float: right; font-size: 10px; padding: 1px 8px; border: 1px solid #d7b9b9; background: #fff; color: #7a1818; border-radius: 10px; cursor: pointer; line-height: 1.6; font-weight: 600; }
.inv-add:hover { background: #faf3f3; }
.inv-rows { display: flex; flex-direction: column; gap: 5px; margin-top: 8px; clear: both; }
.inv-row { display: flex; align-items: center; gap: 6px; }
.inv-row-label { flex: 1 1 auto; min-width: 0; font-size: 11px; }
.inv-row-unit { flex: 0 0 76px; width: 76px; font-size: 11px; text-align: right; }
.inv-del { flex: 0 0 auto; border: none; background: none; color: #c0392b; cursor: pointer; font-size: 13px; padding: 2px 4px; line-height: 1; }
.inv-del:hover { color: #7a1818; }
.inv-empty { font-size: 10px; color: #aaa; margin: 2px 0 0; }
</style>
